import type { FetchMatcher, JsonApiResource, MatcherFixture } from 'tio-data/demo/types';
import { PERSON_ID, USER_ID } from 'tio-employee/demo/identity-fixtures';
import { buildEligibilityJobDetailDoc } from 'tio-employee/demo/admin-fixtures';

// .invalid never resolves (RFC 6761): without the XHR guard the PUT fails instead of leaving.
const UPLOAD_ORIGIN = 'https://demo-upload.invalid';

const UPLOADED_DOCUMENT_ID_PREFIX = 'demo-upload-doc-';

// The TAS models an upload attaches to; tio-api pluralizes each as its collection path.
const ATTACHABLE_COLLECTIONS = 'tas-applications|tas-courses|tas-program-instances';
const ATTACHABLE_CREATE = new RegExp(`/(${ATTACHABLE_COLLECTIONS})(\\?|$)`);
const ATTACHABLE_UPDATE = new RegExp(`/(${ATTACHABLE_COLLECTIONS})/(demo-[^/?]+)(\\?|$)`);
const TAS_ASSET_READ = /\/tas-assets\/([^/?]+)(\?|$)/;
const ELIGIBILITY_JOB_READ = /\/eligibility-file-job-logs\/([^/?]+)(\?|$)/;

interface UploadedAsset {
  id: string;
  bucket: string;
  filename: string;
  name: string;
  fullPath: string;
  contentType: string;
  size: number;
  classification: string;
  signedUrl: string;
}

interface UploadedDocument {
  id: string;
  assetId: string | undefined;
  batch: string | null;
  contentType: string;
  via: string;
  provider: string;
  createdAt: string;
  extractionState: string;
  reflectionState: string;
  subsistenceState: string;
}

interface UploadedEligibilityJob {
  id: string;
  fileName: string;
  strategy: string;
  startTime: string;
  companyId: string;
}

// Page lifetime, like a real upload's server records: a reload starts the demo over.
const UPLOADED_BODIES = new Map<string, Blob>();
const UPLOADED_ASSETS = new Map<string, UploadedAsset>();
const UPLOADED_DOCUMENTS = new Map<string, UploadedDocument>();
const UPLOADED_TAS_ASSETS = new Map<string, JsonApiResource>();
const UPLOADED_ELIGIBILITY_JOBS = new Map<string, UploadedEligibilityJob>();
const DISCARDED_DOCUMENT_IDS = new Set<string>();
const UPLOADED_FILE_URLS = new Set<string>();

/** Forgets every upload and discard made this page load (test isolation), revoking the uploads' object URLs. */
export function resetDemoUploads(): void {
  UPLOADED_FILE_URLS.forEach((url) => URL.revokeObjectURL(url));
  UPLOADED_FILE_URLS.clear();
  UPLOADED_BODIES.clear();
  UPLOADED_ASSETS.clear();
  UPLOADED_DOCUMENTS.clear();
  UPLOADED_TAS_ASSETS.clear();
  UPLOADED_ELIGIBILITY_JOBS.clear();
  DISCARDED_DOCUMENT_IDS.clear();
}

/** Records a document the operator deleted, until the page reloads. */
export function discardDemoDocument(id: string): void {
  DISCARDED_DOCUMENT_IDS.add(id);
}

/** Whether the operator deleted this document during this page load. */
export function isDemoDocumentDiscarded(id: string): boolean {
  return DISCARDED_DOCUMENT_IDS.has(id);
}

/** Keeps bytes the XHR guard answered locally, for the uploaded asset's download link. */
export function rememberDemoUploadBody(url: string, body: unknown): void {
  if (body instanceof Blob) {
    UPLOADED_BODIES.set(url, body);
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown) => (typeof value === 'string' ? value : undefined);

const createdResource = (body: unknown) =>
  isRecord(body) && isRecord(body['data']) ? body['data'] : undefined;

const createdAttributes = (body: unknown) => {
  const attributes = createdResource(body)?.['attributes'];
  return isRecord(attributes) ? attributes : {};
};

function relatedId(body: unknown, relationship: string): string | undefined {
  const relationships = createdResource(body)?.['relationships'];
  const related = isRecord(relationships) ? relationships[relationship] : undefined;
  const data = isRecord(related) ? related['data'] : undefined;
  return isRecord(data) ? text(data['id']) : undefined;
}

// Demo fixtures mint every id with this prefix; tio-api ids never carry it.
const isDemoId = (id: unknown) => typeof id === 'string' && id.startsWith('demo-');

function linkageIds(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data.flatMap(linkageIds);
  }
  return isRecord(data) ? [data['id']] : [];
}

// Keyed to the demo identity: every record the create links to came from the demo fixtures.
function linksOnlyDemoRecords(body: unknown): boolean {
  const relationships = createdResource(body)?.['relationships'];
  const ids = isRecord(relationships)
    ? Object.values(relationships).flatMap((related) =>
        isRecord(related) ? linkageIds(related['data']) : []
      )
    : [];
  return ids.length > 0 && ids.every(isDemoId);
}

const singular = (collection: string) => collection.replace(/s$/, '');

// tio-api CloudfrontSignerService#formatted_filename.
const s3Filename = (name: string) => name.replace(/[^0-9A-Z.-]/gi, '_').toLowerCase();

const uploadTarget = (uuid: string, filename: string) => `${UPLOAD_ORIGIN}/${uuid}/${filename}`;

// tio-api EligibilityFilesController#test_in_filename?, which turns a MANUAL upload into TEST_MANUAL.
function isTestFilename(fileName: string): boolean {
  const name = (fileName.split('/').at(-1) ?? '').toLowerCase();
  return name.startsWith('test') || /[^a-z0-9]test[^a-z0-9].*\.csv$|[^a-z0-9]test\.csv$/.test(name);
}

function uploadedFileUrl(uuid: string, filename: string): string {
  const target = uploadTarget(uuid, filename);
  const bytes = UPLOADED_BODIES.get(target);
  if (!bytes) {
    return target;
  }
  const url = URL.createObjectURL(bytes);
  UPLOADED_FILE_URLS.add(url);
  return url;
}

// tio-api ObservableAsset#ensure_classification: a PDF is narrowed later by the file processor.
const classifyByContentType = (contentType: string) =>
  contentType === 'text/plain' ? 'ObservableProvider.NSLDS' : 'ObservableProvider.AMBIGUOUS';

// tio-api ObservableDocument#dispatch at create: proxy and self-reported entries skip extraction.
function initialStates(via: string, provider: string) {
  const unreflected = {
    reflectionState: 'ReflectionState.NOT_APPLIED',
    subsistenceState: 'SubsistenceState.IMPLICIT',
  };
  if (via === 'PROXY') {
    return {
      ...unreflected,
      extractionState: 'ExtractionState.PROXY',
      provider: 'ObservableProvider.PROXY',
    };
  }
  if (via === 'SELF_REPORTED') {
    return {
      ...unreflected,
      extractionState: 'ExtractionState.PROXY',
      provider: 'ObservableProvider.PERSON',
    };
  }
  return { ...unreflected, extractionState: 'ExtractionState.DISPATCHED', provider };
}

function signedUrlMatcher(bucket: 'observable-assets' | 'digital-assets'): FetchMatcher {
  const path = new RegExp(`/${bucket}/s3-signed-url(\\?|$)`);
  return {
    urlMatches: (url: string) => path.test(url),
    method: 'POST',
    bodyMatches: (body: unknown) => isRecord(body) && text(body['filename']) !== undefined,
    json: (_url: string, body?: unknown) => {
      const name = (isRecord(body) && text(body['filename'])) || '';
      const filename = s3Filename(name);
      const uuid = crypto.randomUUID();
      const day = new Date().toISOString().slice(0, 10).replaceAll('-', '/');
      return {
        uuid,
        bucket,
        filename,
        key: `uploads/${day}/${uuid}/${filename}`,
        name,
        url: uploadTarget(uuid, filename),
      };
    },
  };
}

function assetResource(asset: UploadedAsset, documentId?: string): JsonApiResource {
  const { id, ...attributes } = asset;
  return {
    type: 'observable-asset',
    id,
    attributes: { uuid: id, ...attributes, externalUrl: null, description: null },
    ...(documentId
      ? {
          relationships: {
            observableDocument: { data: { type: 'observable-document', id: documentId } },
          },
        }
      : {}),
  };
}

function documentAttributes(document: UploadedDocument) {
  return {
    batch: document.batch,
    contentType: document.contentType,
    via: document.via,
    provider: document.provider,
    extractionState: document.extractionState,
    reflectionState: document.reflectionState,
    subsistenceState: document.subsistenceState,
    extractedContent: null,
    structuredContent: null,
    providedAt: null,
    createdAt: document.createdAt,
  };
}

/** What a read serves over an upload's create-time attributes; undefined keeps them. */
export type DemoUploadReadBack = (upload: {
  id: string;
  via: string;
  provider: string;
  extractionState: string;
  createdAt: string;
  filename: string;
}) => Record<string, unknown> | undefined;

function uploadedEntry(document: UploadedDocument, readAs?: DemoUploadReadBack) {
  const asset = document.assetId ? UPLOADED_ASSETS.get(document.assetId) : undefined;
  const readBack = readAs?.({ ...document, filename: asset?.filename ?? '' });
  const resource: JsonApiResource = {
    type: 'observable-document',
    id: document.id,
    attributes: { ...documentAttributes(document), ...readBack },
    relationships: {
      observableAsset: { data: asset ? { type: 'observable-asset', id: asset.id } : null },
      person: { data: { type: 'person', id: PERSON_ID } },
    },
  };
  return { document: resource, asset: asset ? assetResource(asset, document.id) : undefined };
}

/** This page load's uploaded documents, newest first, each with its asset to sideload. */
export function demoUploadedDocuments(readAs?: DemoUploadReadBack) {
  return [...UPLOADED_DOCUMENTS.values()]
    .reverse()
    .map((document) => uploadedEntry(document, readAs));
}

/** One uploaded document and its asset by id; undefined if this page load made none. */
export function demoUploadedDocument(id: string, readAs?: DemoUploadReadBack) {
  const document = UPLOADED_DOCUMENTS.get(id);
  return document ? uploadedEntry(document, readAs) : undefined;
}

function tasAssetMatchers(): FetchMatcher[] {
  const registeredAsset = (url: string) => {
    const id = TAS_ASSET_READ.exec(url)?.[1];
    return id ? UPLOADED_TAS_ASSETS.get(id) : undefined;
  };
  return [
    // TAS attachments and CourseCash receipts: the asset create that links the upload to its records.
    {
      urlMatches: (url: string) => /\/tas-assets(\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: linksOnlyDemoRecords,
      json: (_url: string, body?: unknown) => {
        const attributes = createdAttributes(body);
        const uuid = text(attributes['uuid']) ?? crypto.randomUUID();
        const filename = text(attributes['filename']) ?? '';
        // tio-api reads size and type off the stored object when the client sends neither.
        const bytes = UPLOADED_BODIES.get(uploadTarget(uuid, filename));
        const asset: JsonApiResource = {
          type: 'tas-asset',
          id: `demo-upload-tas-asset-${uuid}`,
          attributes: {
            uuid,
            bucket: text(attributes['bucket']) ?? 'digital-assets',
            filename,
            fullPath: text(attributes['fullPath']) ?? '',
            name: text(attributes['name']) ?? filename,
            size: bytes?.size ?? 0,
            signedUrl: uploadedFileUrl(uuid, filename),
            contentType: bytes?.type || null,
            classification: text(attributes['classification']) ?? null,
            description: null,
            tags: isRecord(attributes['tags']) ? attributes['tags'] : {},
            createdAt: new Date().toISOString(),
            attachmentDeletedAt: null,
          },
        };
        UPLOADED_TAS_ASSETS.set(asset.id, asset);
        return { data: asset };
      },
    },
    {
      urlMatches: (url: string) => registeredAsset(url) !== undefined,
      method: 'GET',
      json: (url: string) => ({ data: registeredAsset(url) ?? null }),
    },
    // Tag writes after the upload (use anyways, the analysis timeout) land on the registered asset.
    {
      urlMatches: (url: string) => registeredAsset(url) !== undefined,
      method: 'PATCH',
      bodyMatches: (body: unknown) => createdResource(body) !== undefined,
      json: (url: string, body?: unknown) => {
        const asset = registeredAsset(url);
        if (!asset) {
          return { data: null };
        }
        const updated = {
          ...asset,
          attributes: { ...asset.attributes, ...createdAttributes(body) },
        };
        UPLOADED_TAS_ASSETS.set(updated.id, updated);
        return { data: updated };
      },
    },
    // A parent the upload attaches to is created first when it is still new.
    {
      urlMatches: (url: string) => ATTACHABLE_CREATE.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: linksOnlyDemoRecords,
      json: (url: string, body?: unknown) => {
        const type = singular(ATTACHABLE_CREATE.exec(url)?.[1] ?? '');
        return {
          data: {
            type,
            id: `demo-upload-${type}-${crypto.randomUUID()}`,
            attributes: createdAttributes(body),
          },
        };
      },
    },
    // The parent's save after the link; the store keeps its own relationships.
    {
      urlMatches: (url: string) => ATTACHABLE_UPDATE.test(url),
      method: 'PATCH',
      bodyMatches: (body: unknown) => createdResource(body) !== undefined,
      json: (url: string, body?: unknown) => {
        const [, collection = '', id = ''] = ATTACHABLE_UPDATE.exec(url) ?? [];
        return { data: { type: singular(collection), id, attributes: createdAttributes(body) } };
      },
    },
  ];
}

function selfReportedTransactions(body: unknown) {
  const payload = isRecord(body) ? body : {};
  const documentId = text(payload['observableDocumentId']) ?? '';
  const now = new Date().toISOString();
  const transactions = Array.isArray(payload['transactions']) ? payload['transactions'] : [];
  return transactions.filter(isRecord).map((transaction) => ({
    type: 'account-transaction',
    id: `demo-upload-txn-${crypto.randomUUID()}`,
    attributes: {
      amount: transaction['amount'],
      feeAmount: 0,
      interestAmount: 0,
      principalAmount: 0,
      transactionDate: transaction['transactionDate'],
      postedDate: null,
      // tio-api derives the category from the type: a PAYMENT is a DEBIT.
      category: 'TransactionCategory.DEBIT',
      transactionType: transaction['transactionType'],
      aggregationTransactionType: null,
      aggregationTransactionId: documentId,
      // A personal-scope create requests verification and stays pending until an admin reviews it.
      verificationState: 'TransactionVerificationState.PENDING_VERIFICATION',
      transitionLog: [
        {
          from: 'TransactionVerificationState.CREATED',
          to: 'TransactionVerificationState.PENDING_VERIFICATION',
          event: 'request_verification',
          reason: { note: 'automated system transition' },
          timestamp: now,
          user_id: null,
        },
      ],
      createdAt: now,
      deletedAt: null,
      reversed: false,
      duplicateAggregate: false,
    },
  }));
}

// tio-api answers 422 when an employee sets an institution or reports anything but a payment.
function isSelfReportedBulk(body: unknown): boolean {
  if (!isRecord(body) || body['personId'] !== PERSON_ID || body['financialInstitutionId']) {
    return false;
  }
  const document = UPLOADED_DOCUMENTS.get(text(body['observableDocumentId']) ?? '');
  const transactions = body['transactions'];
  return (
    document !== undefined &&
    Array.isArray(transactions) &&
    transactions.length > 0 &&
    transactions.every(
      (transaction) =>
        isRecord(transaction) &&
        transaction['transactionType'] === 'TransactionType.PAYMENT' &&
        !transaction['financialInstitutionId']
    )
  );
}

function eligibilityJobCreated(body: unknown) {
  const payload = isRecord(body) ? body : {};
  const fileName = text(payload['file-name']) ?? '';
  const requested = text(payload['strategy']) || 'MANUAL';
  const strategy = requested === 'MANUAL' && isTestFilename(fileName) ? 'TEST_MANUAL' : requested;
  const processId = crypto.randomUUID();
  const now = new Date().toISOString();
  const companyId = text(payload['company_id']) ?? '';
  // The demo partner company has no legacy id, so its key falls back to the company id.
  const filePath = `partition_date=${now.slice(0, 10)}/company_id=${text(payload['legacy_id']) || companyId}/${processId}`;
  const job: UploadedEligibilityJob = {
    id: `demo-upload-elig-${processId}`,
    fileName,
    strategy,
    startTime: now,
    companyId,
  };
  UPLOADED_ELIGIBILITY_JOBS.set(job.id, job);
  const services = {};
  return {
    data: {
      type: 'eligibility-file-job-log',
      id: job.id,
      attributes: {
        processId,
        companyId,
        fileName,
        uploadUrl: `${UPLOAD_ORIGIN}/${filePath}.csv`,
        status: 'WAITING_FOR_UPLOAD',
        strategy,
        startTime: now,
        endTime: null,
        files: {
          rawFilePath: `${filePath}.csv`,
          cleanedFilePath: `${filePath}/${processId}-all.json`,
          createdFilePath: `${filePath}/${processId}-creates.txt`,
          deactivatedFilePath: `${filePath}/${processId}-deactivates.txt`,
          reactivatedFilePath: `${filePath}/${processId}-reactivates.txt`,
          updatedFilePath: `${filePath}/${processId}-updates.txt`,
        },
        counts: {
          created: 0,
          createdSentEmail: 0,
          deactivated: 0,
          loaded: 0,
          reactivated: 0,
          updated: 0,
          services,
        },
        previewCounts: { created: 0, deactivated: 0, reactivated: 0, updated: 0, services },
        statusHistory: [
          { status: 'STARTED', updatedAt: now, updatedBy: { id: USER_ID } },
          { status: 'WAITING_FOR_UPLOAD', updatedAt: now, updatedBy: { id: USER_ID } },
        ],
        jobErrors: null,
        metaDiff: null,
        header: null,
        createdAt: now,
        createdBy: USER_ID,
        updatedAt: now,
        updatedBy: USER_ID,
      },
    },
  };
}

/** Request-handler matchers for SkipCache upload builders: the admin eligibility upload URL. */
export function buildUploadMatchers(): MatcherFixture[] {
  return [
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: (url: string) => /\/eligibility-files\/get-upload-url(\?|$)/.test(url),
        // The admin page sends the demo partner company (demo-company-<client>), which has no legacy id.
        bodyMatches: (body: unknown) =>
          isRecord(body) && isDemoId(body['company_id']) && text(body['file-name']) !== undefined,
      },
      contentFor: eligibilityJobCreated,
    },
  ];
}

// The TAS, SYF, eligibility and remote-event requests around an upload.
function uploadFlowMatchers(): FetchMatcher[] {
  const uploadedJob = (url: string) => {
    const id = ELIGIBILITY_JOB_READ.exec(url)?.[1];
    return id ? UPLOADED_ELIGIBILITY_JOBS.get(id) : undefined;
  };
  return [
    ...tasAssetMatchers(),
    {
      urlMatches: (url: string) => /\/account-transactions\/bulk(\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: isSelfReportedBulk,
      json: (_url: string, body?: unknown) => ({ data: selfReportedTransactions(body) }),
    },
    // The job page an eligibility upload lands on, already finished so it never polls.
    {
      urlMatches: (url: string) => uploadedJob(url) !== undefined,
      method: 'GET',
      json: (url: string) => {
        const job = uploadedJob(url);
        if (!job) {
          return { data: null };
        }
        return buildEligibilityJobDetailDoc({
          ...job,
          status: job.strategy === 'TEST_MANUAL' ? 'TEST_COMPLETED' : 'COMPLETED',
          endTime: job.startTime,
        });
      },
    },
    {
      urlMatches: (url: string) => /\/remote-events(\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: (body: unknown) => relatedId(body, 'user') === USER_ID,
      json: (_url: string, body?: unknown) => {
        const attributes = createdAttributes(body);
        return {
          data: {
            type: 'remote-event',
            id: `demo-remote-event-${crypto.randomUUID()}`,
            attributes: {
              eventType: attributes['eventType'] ?? null,
              payload: attributes['payload'] ?? {},
              version: 0,
              at: new Date().toISOString(),
            },
          },
        };
      },
    },
  ];
}

/** Fetch-seam matchers for both signed-URL endpoints, the observable creates and the other upload flows. */
export function buildUploadFetchMatchers(): FetchMatcher[] {
  return [
    signedUrlMatcher('observable-assets'),
    signedUrlMatcher('digital-assets'),
    {
      urlMatches: (url: string) => /\/observable-assets(\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: (body: unknown) => createdResource(body) !== undefined,
      json: (_url: string, body?: unknown) => {
        const attributes = createdAttributes(body);
        const id = text(attributes['uuid']) ?? crypto.randomUUID();
        const filename = text(attributes['filename']) ?? '';
        const contentType = text(attributes['contentType']) ?? '';
        const asset: UploadedAsset = {
          id,
          bucket: text(attributes['bucket']) ?? 'observable-assets',
          filename,
          name: text(attributes['name']) ?? filename,
          fullPath: text(attributes['fullPath']) ?? '',
          contentType,
          size: typeof attributes['size'] === 'number' ? attributes['size'] : 0,
          classification: text(attributes['classification']) || classifyByContentType(contentType),
          signedUrl: uploadedFileUrl(id, filename),
        };
        UPLOADED_ASSETS.set(id, asset);
        return { data: assetResource(asset) };
      },
    },
    {
      urlMatches: (url: string) => /\/observable-documents(\?|$)/.test(url),
      method: 'POST',
      status: 201,
      bodyMatches: (body: unknown) => createdResource(body) !== undefined,
      json: (_url: string, body?: unknown) => {
        const attributes = createdAttributes(body);
        const via = text(attributes['via']) ?? 'UPLOAD';
        const document: UploadedDocument = {
          // Fresh per create, so a second upload never reuses a committed record's id.
          id: `${UPLOADED_DOCUMENT_ID_PREFIX}${crypto.randomUUID()}`,
          assetId: relatedId(body, 'observableAsset'),
          batch: text(attributes['batch']) ?? null,
          contentType: text(attributes['contentType']) ?? '',
          via,
          createdAt: new Date().toISOString(),
          ...initialStates(via, text(attributes['provider']) ?? 'ObservableProvider.AMBIGUOUS'),
        };
        UPLOADED_DOCUMENTS.set(document.id, document);
        return {
          data: {
            type: 'observable-document',
            id: document.id,
            attributes: documentAttributes(document),
          },
        };
      },
    },
    ...uploadFlowMatchers(),
  ];
}
